export const RESOURCE_SCALE = 1_000_000n
export const NORMAL_SPEED_BPS = 10_000n

export interface ResourceProjectionAnchor {
  amountMicro: number
  projectionRemainder: number
  productionRateMicroPerSecond: number
  capacityMicro: number
}

function integer(value: number, label: string): bigint {
  if (!Number.isSafeInteger(value))
    throw new RangeError(`${label}_NOT_SAFE_INTEGER`)
  return BigInt(value)
}

function safeNumber(value: bigint, label: string): number {
  const result = Number(value)
  if (!Number.isSafeInteger(result))
    throw new RangeError(`${label}_NOT_SAFE_INTEGER`)
  return result
}

export function worldElapsedUs(
  realElapsedUs: number,
  speedBps: number,
): number {
  const elapsed = integer(
    Math.max(0, Math.floor(realElapsedUs)),
    'REAL_ELAPSED_US',
  )
  const speed = integer(speedBps, 'SPEED_BPS')
  if (speed <= 0n) throw new RangeError('SPEED_BPS_INVALID')
  return safeNumber((elapsed * speed) / NORMAL_SPEED_BPS, 'WORLD_ELAPSED_US')
}

export function worldElapsedUsFromPerformanceMs(
  elapsedPerformanceMs: number,
  speedBps: number,
): number {
  if (!Number.isFinite(elapsedPerformanceMs)) return 0
  return worldElapsedUs(
    Math.floor(Math.max(0, elapsedPerformanceMs) * 1000),
    speedBps,
  )
}

export interface ResourceProjectionState {
  amountMicro: number
  remainder: number
  creditedMicro: number
  lostMicro: number
}

export function projectResourceState(
  anchor: ResourceProjectionAnchor,
  elapsedWorldTimeUs: number,
): ResourceProjectionState {
  const amount = integer(anchor.amountMicro, 'AMOUNT_MICRO')
  const remainder = integer(anchor.projectionRemainder, 'PROJECTION_REMAINDER')
  const rate = integer(anchor.productionRateMicroPerSecond, 'PRODUCTION_RATE')
  const capacity = integer(anchor.capacityMicro, 'CAPACITY_MICRO')
  const elapsed = integer(
    Math.max(0, Math.floor(elapsedWorldTimeUs)),
    'ELAPSED_WORLD_TIME_US',
  )

  if (amount < 0n || remainder < 0n || rate < 0n || capacity < 0n)
    throw new RangeError('RESOURCE_PROJECTION_NEGATIVE_INPUT')

  if (remainder >= RESOURCE_SCALE)
    throw new RangeError('PROJECTION_REMAINDER_INVALID')
  const wholeSeconds = elapsed / RESOURCE_SCALE
  const remainingUs = elapsed % RESOURCE_SCALE
  const whole = rate * wholeSeconds
  const fractionNumerator = rate * remainingUs + remainder
  const delta = whole + fractionNumerator / RESOURCE_SCALE
  const nextRemainder = fractionNumerator % RESOURCE_SCALE
  if (amount >= capacity) {
    return {
      amountMicro: safeNumber(amount, 'PROJECTED_AMOUNT'),
      remainder: 0,
      creditedMicro: 0,
      lostMicro: safeNumber(delta, 'LOST_MICRO'),
    }
  }
  const credited = delta < capacity - amount ? delta : capacity - amount
  const lost = delta - credited
  const projected = amount + credited
  return {
    amountMicro: safeNumber(projected, 'PROJECTED_AMOUNT'),
    remainder:
      lost > 0n || projected >= capacity
        ? 0
        : safeNumber(nextRemainder, 'PROJECTION_REMAINDER'),
    creditedMicro: safeNumber(credited, 'CREDITED_MICRO'),
    lostMicro: safeNumber(lost, 'LOST_MICRO'),
  }
}

export function projectResourceMicro(
  anchor: ResourceProjectionAnchor,
  elapsedWorldTimeUs: number,
): number {
  return projectResourceState(anchor, elapsedWorldTimeUs).amountMicro
}

export function realDelayMsForWorldDelta(
  worldDeltaUs: number,
  speedBps: number,
): number {
  const delta = integer(Math.max(0, Math.floor(worldDeltaUs)), 'WORLD_DELTA_US')
  const speed = integer(speedBps, 'SPEED_BPS')
  if (speed <= 0n) throw new RangeError('SPEED_BPS_INVALID')
  const realUs = (delta * NORMAL_SPEED_BPS + speed - 1n) / speed
  return Math.max(0, safeNumber(realUs, 'REAL_DELAY_US') / 1000)
}
